/**
 * Custom element to track Kaching Bundle discounts and add them as line item properties.
 *
 * @extends HTMLElement
 */

class KachingTracking extends HTMLElement {
	/** @private */
	#selectors = {
		bundlesBlock: 'kaching-bundles-block',
		bundlesBlockAlt: 'kaching-bundle',
		bundleBar: '.kaching-bundles__bar',
		price: '.kaching-bundles__bar-price',
		// L'app a renommé sa classe (compare-at-price → full-price) — on garde
		// l'ancien sélecteur en fallback pour absorber les versions.
		compareAtPrice: '.kaching-bundles__bar-full-price, .kaching-bundles__bar-compare-at-price',
		form: 'form[action*="/cart/add"]',
	};

	/** @private */
	#properties = {
		discount: '__kaching_discount',
		percent: '__kaching_percent',
		uniqueId: '__kaching_unique_id',
	};

	/** @type {HTMLElement|null} */
	#bundlesBlock = null;

	/** @type {HTMLFormElement|null} */
	#form = null;

	/** @type {AbortController|null} */
	#abortController = null;

	/** @type {MutationObserver|null} */
	#renderObserver = null;

	/** @type {number|null} */
	#renderRaf = null;

	constructor() {
		super();
	}

	connectedCallback() {
		this.#init();
	}

	disconnectedCallback() {
		this.#cleanup();
	}

	/**
	 * Initialize the component.
	 * @private
	 * @returns {void}
	 */
	#init() {
		this.#cacheElements();

		if (!this.#isValidSetup()) {
			// If setup is not valid yet, it might be because Kaching is loading dynamically.
			// The MutationObserver in product.js (if any) or a short delay could help,
			// but we'll try to find it again when it appears.
			this.#observerKaching();
			return;
		}

		this.#setupEventListeners();
	}

	/**
	 * Cache DOM elements.
	 * @private
	 * @returns {void}
	 */
	#cacheElements() {
		this.#bundlesBlock =
			document.querySelector(this.#selectors.bundlesBlock) ||
			document.querySelector(this.#selectors.bundlesBlockAlt);
		this.#form = this.closest('form') || document.querySelector(this.#selectors.form);
	}

	/**
	 * Validate if the setup is ready.
	 * @private
	 * @returns {boolean}
	 */
	#isValidSetup() {
		return !!(this.#bundlesBlock && this.#form);
	}

	/**
	 * Observe the DOM for Kaching bundles block if not found initially.
	 * @private
	 * @returns {void}
	 */
	#observerKaching() {
		const observer = new MutationObserver(() => {
			this.#cacheElements();
			if (this.#isValidSetup()) {
				this.#setupEventListeners();
				observer.disconnect();
			}
		});

		observer.observe(document.body, { childList: true, subtree: true });
	}

	/**
	 * Setup event listeners.
	 * @private
	 * @returns {void}
	 */
	#setupEventListeners() {
		if (this.#abortController) this.#abortController.abort();
		this.#abortController = new AbortController();
		const { signal } = this.#abortController;

		// The documentation provided says the event is on 'kaching-bundles-block'
		this.#bundlesBlock.addEventListener(
			'variants-changed',
			(e) => {
				// Defer to let Kaching finish its DOM updates before we scrape prices
				requestAnimationFrame(() => this.#handleUpdate(e.detail));
			},
			{ signal },
		);

		this.#bundlesBlock.addEventListener(
			'deal-bar-selected',
			(e) => {
				// Defer to let Kaching finish its DOM updates before we scrape prices
				requestAnimationFrame(() => this.#handleUpdate(e.detail));
			},
			{ signal },
		);

		// Also listen for form submission to refresh the unique ID for the NEXT add
		// This ensures that if the user clicks "Add to cart" twice on the same pack,
		// they are treated as separate line items.
		this.#form.addEventListener(
			'submit',
			() => {
				// Small delay to ensure the current submission uses the current ID
				// before we generate a new one for the next possible click.
				setTimeout(() => this.#handleUpdate(), 100);
			},
			{ signal },
		);

		// Initial check
		this.#handleUpdate();

		// L'app rend (et re-rend) ses barres de deal APRÈS notre init, et la
		// présélection initiale n'émet aucun event — sans ce re-check, les
		// props ERP manquent si le client ajoute au panier sans changer de
		// palier. Coalescé via rAF ; les inputs sont posés dans le form (hors
		// du bloc observé), donc pas de boucle.
		this.#renderObserver = new MutationObserver(() => {
			if (this.#renderRaf) return;
			this.#renderRaf = requestAnimationFrame(() => {
				this.#renderRaf = null;
				this.#handleUpdate();
			});
		});
		this.#renderObserver.observe(this.#bundlesBlock, { childList: true, subtree: true });
	}

	/**
	 * Handle updates from Kaching events.
	 * @private
	 * @param {object} [detail] - Event detail if triggered by an event.
	 * @returns {void}
	 */
	#handleUpdate(detail = {}) {
		let prices = null;

		// 1. Try to get pricing from event detail (most robust)
		if (detail.pricing) {
			const fullPriceObj = detail.pricing.fullPrice || detail.pricing.totalBeforeDiscount;
			const discountedPriceObj =
				detail.pricing.discountedPrice || detail.pricing.totalAfterDiscount;

			const getAmount = (obj) => {
				if (typeof obj === 'number') return obj / 100;
				if (obj && typeof obj === 'object') {
					const val = obj.amount !== undefined ? obj.amount : obj.value;
					return typeof val === 'number' ? val / 100 : parseFloat(val) / 100;
				}
				return NaN;
			};

			const compareAt = getAmount(fullPriceObj);
			const price = getAmount(discountedPriceObj);

			if (!isNaN(price) && !isNaN(compareAt)) {
				prices = { price, compareAt };
			}
		}

		// 2. Fallback to DOM scraping if event detail is missing or incomplete
		if (!prices) {
			const selectedBar = this.#getSelectedBar();
			if (selectedBar) {
				prices = this.#extractPrices(selectedBar);
			}
		}

		if (!prices) {
			this.#removeTrackingInputs();
			return;
		}

		const discount = prices.compareAt - prices.price;

		if (discount <= 0) {
			this.#removeTrackingInputs();
			return;
		}

		const percentage = (discount / prices.compareAt) * 100;

		this.#updateTrackingInputs(discount, percentage);
	}

	/**
	 * Generate a unique ID for the bundle addition.
	 * @private
	 * @returns {string}
	 */
	#generateUniqueId() {
		return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	}

	/**
	 * Find the currently selected deal bar in the Kaching block.
	 * Tries multiple selection strategies since Kaching's DOM is not fully documented.
	 * @private
	 * @returns {HTMLElement|null}
	 */
	#getSelectedBar() {
		const bar = this.#selectors.bundleBar;
		// Try class/attribute-based selection first (bar element itself is the match)
		const directSelectors = [
			`${bar}--selected`,
			`${bar}.selected`,
			`${bar}.is-selected`,
			`${bar}[aria-checked="true"]`,
			`${bar}[data-selected]`,
			`${bar}[data-selected="true"]`,
		];
		for (const sel of directSelectors) {
			const el = this.#bundlesBlock.querySelector(sel);
			if (el) return el;
		}
		// Fallback: find a checked radio inside a bar
		const checkedInput = this.#bundlesBlock.querySelector(`${bar} input[type="radio"]:checked`);
		if (checkedInput) return checkedInput.closest(bar);

		return null;
	}

	/**
	 * Extract prices from the selected bar.
	 * @private
	 * @param {HTMLElement} bar - The selected deal bar element.
	 * @returns {{price: number, compareAt: number}|null}
	 */
	#extractPrices(bar) {
		const priceEl = bar.querySelector(this.#selectors.price);
		const compareAtEl = bar.querySelector(this.#selectors.compareAtPrice);

		if (!priceEl) return null;

		const price = this.#parsePrice(priceEl.textContent);
		// If compareAt is not present, use price (no discount)
		const compareAt = compareAtEl ? this.#parsePrice(compareAtEl.textContent) : price;

		if (isNaN(price) || isNaN(compareAt)) return null;

		return { price, compareAt };
	}

	/**
	 * Parse price string to number.
	 * @private
	 * @param {string} priceString - The price string (e.g., "15,85 €").
	 * @returns {number}
	 */
	#parsePrice(priceString) {
		if (!priceString) return NaN;
		// Remove currency and handle common decimal separators
		const cleaned = priceString.replace(/[^0-9,.]/g, '').replace(',', '.');
		return parseFloat(cleaned);
	}

	/**
	 * Update or create hidden inputs in the form.
	 * @private
	 * @param {number} discount - The absolute discount amount.
	 * @param {number} percentage - The discount percentage.
	 * @returns {void}
	 */
	#updateTrackingInputs(discount, percentage) {
		this.#ensureInput(this.#properties.discount, discount.toFixed(2));
		this.#ensureInput(this.#properties.percent, percentage.toFixed(2));
		this.#ensureInput(this.#properties.uniqueId, this.#generateUniqueId());
	}

	/**
	 * Ensure a hidden input exists with the correct value.
	 * @private
	 * @param {string} name - The property name.
	 * @param {string} value - The property value.
	 * @returns {void}
	 */
	#ensureInput(name, value) {
		const inputName = `properties[${name}]`;
		let input = this.#form.querySelector(`input[name="${inputName}"]`);

		if (!input) {
			input = document.createElement('input');
			input.type = 'hidden';
			input.name = inputName;
			this.#form.appendChild(input);
		}

		input.value = value;
	}

	/**
	 * Remove tracking hidden inputs from the form.
	 * @private
	 * @returns {void}
	 */
	#removeTrackingInputs() {
		Object.values(this.#properties).forEach((name) => {
			const input = this.#form.querySelector(`input[name="properties[${name}]"]`);
			if (input) input.remove();
		});
	}

	/**
	 * Cleanup resources.
	 * @private
	 * @returns {void}
	 */
	#cleanup() {
		if (this.#abortController) {
			this.#abortController.abort();
			this.#abortController = null;
		}
		if (this.#renderObserver) {
			this.#renderObserver.disconnect();
			this.#renderObserver = null;
		}
		if (this.#renderRaf) {
			cancelAnimationFrame(this.#renderRaf);
			this.#renderRaf = null;
		}
		this.#removeTrackingInputs();
	}
}

if (!customElements.get('kaching-tracking')) {
	customElements.define('kaching-tracking', KachingTracking);
}
